import { FC } from 'react';
import { Clock, PoundSterling, Users, Check } from 'lucide-react';
import { Card } from '../../../components/ui/Card';
import { Badge } from '../../../components/ui/Badge';
import { Spinner } from '../../../components/ui/Spinner';
import { cn } from '../../../utils/cn';
import { useServices } from '../../../hooks/useServices';
import { useBooking } from '../../../contexts/BookingContext';
import { Service } from '../../../types/service.types';

export const Step1ServiceSelection: FC = () => {
  const { formData, updateFormData, nextStep } = useBooking();
  const { data: services, isLoading, error } = useServices({}, 'popular');

  const handleServiceSelect = (service: Service) => {
    updateFormData({ serviceId: service.id });
    // Auto-advance to next step after short delay for visual feedback
    setTimeout(() => {
      nextStep();
    }, 150);
  };

  if (isLoading) {
    return (
      <div className="flex min-h-[300px] items-center justify-center">
        <Spinner size="lg" color="primary" label="Loading services..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className="rounded-lg border border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20 p-6 text-center">
        <p className="text-red-700 dark:text-red-400">Failed to load services. Please try again.</p>
      </div>
    );
  }

  if (!services || services.length === 0) {
    return (
      <div className="rounded-lg border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 p-6 text-center">
        <p className="text-gray-600 dark:text-slate-400">No services available at the moment.</p>
      </div>
    );
  }

  // Group services by category
  const servicesByCategory = services.reduce(
    (acc, service) => {
      const category = service.category;
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(service);
      return acc;
    },
    {} as Record<string, Service[]>
  );

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-slate-100">
          What type of massage would you like?
        </h2>
        <p className="mt-1 text-gray-600 dark:text-slate-400">
          Choose from our range of professional massage therapy services.
        </p>
      </div>

      {Object.entries(servicesByCategory).map(([category, categoryServices]) => (
        <div key={category} className="mb-8 last:mb-0">
          <h3 className="mb-4 text-lg font-medium text-gray-800 dark:text-slate-200">{category}</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            {categoryServices.map((service) => {
              const isSelected = formData.serviceId === service.id;

              return (
                <Card
                  key={service.id}
                  variant="outlined"
                  padding="none"
                  hoverable
                  clickable
                  onClick={() => handleServiceSelect(service)}
                  className={cn(
                    'cursor-pointer transition-all overflow-hidden',
                    isSelected && 'ring-2 ring-primary-500 border-primary-500'
                  )}
                  role="button"
                  aria-pressed={isSelected}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleServiceSelect(service);
                    }
                  }}
                >
                  <div className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="font-semibold text-gray-900 dark:text-slate-100 truncate">{service.name}</h4>
                          {isSelected && (
                            <span className="flex h-5 w-5 items-center justify-center rounded-full bg-primary-500">
                              <Check className="h-3 w-3 text-white" aria-hidden="true" />
                            </span>
                          )}
                        </div>
                        <p className="mt-1 text-sm text-gray-600 dark:text-slate-400 line-clamp-2">
                          {service.description}
                        </p>
                      </div>
                    </div>

                    <div className="mt-4 flex flex-wrap items-center gap-3">
                      <Badge variant="secondary" size="sm">
                        <Clock className="mr-1 h-3 w-3" aria-hidden="true" />
                        {service.duration} min
                      </Badge>
                      <Badge variant="primary" size="sm">
                        <PoundSterling className="mr-1 h-3 w-3" aria-hidden="true" />
                        {service.basePrice}
                      </Badge>
                      {service.availableTherapistCount !== undefined &&
                        service.availableTherapistCount > 0 && (
                          <Badge variant="success" size="sm">
                            <Users className="mr-1 h-3 w-3" aria-hidden="true" />
                            {service.availableTherapistCount} therapist
                            {service.availableTherapistCount > 1 ? 's' : ''}
                          </Badge>
                        )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};
